import React, { useEffect, useRef, useState } from 'react';
import { FormInput, IconSmallArrow, OrangeButton, SmallButton } from 'components';
import { Form, Input } from 'antd';
import Logo from 'assets/images/logo.svg';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { forgotPassword, resetPassword, verifyCode } from 'api/api';
import { useNavigate } from 'react-router-dom';
import { ROLE_TYPES } from 'api/constants';

import LogoClean from 'assets/images/pf25-logo-clean.png';

export const ForgotPassword = ({ showLogin, domain, isMobile = false }) => {
  const [userEmail, setUserEmail] = useState('');
  const [verifiedCode, setVerifiedCode] = useState(null);
  const navigate = useNavigate();

  const handleEmailSubmission = async (email) => {
    const res = await forgotPassword(email, domain);

    if (res?.status === 201) {
      setUserEmail(email);
    }
  };

  const handleVerify = async (code) => {
    const formData = { email: userEmail, code };
    const res = await verifyCode(formData, domain);

    if (res?.status === 200) {
      setVerifiedCode(res.data.code);
    }
  };

  const handleResetPassword = async (password) => {
    const formData = { email: userEmail, passwd: password, code: verifiedCode };
    const res = await resetPassword(formData, domain);

    if (res.status === 201) {
      toast.success('Password changed successfully');
      setUserEmail('');
      setVerifiedCode(null);
      showLogin();
      return navigate(domain === ROLE_TYPES.client ? '/login' : '/');
    }
  };

  const storeModalBody = () => {
    const subtextClass = isMobile
      ? 'text-lightBlue text-14 leading-[22px] max-w-[300px] text-center'
      : 'text-lightBlue text-base leading-[24px] max-w-[350px] text-center';

    if (userEmail) {
      return verifiedCode ? (
        <>
          <p className={subtextClass}>
            Set the new password for Your account
          </p>
          <ResetPassword reset={handleResetPassword} isMobile={isMobile} />
        </>
      ) : (
        <>
          <p className={subtextClass}>
            Enter the 4 digit code that you received on Your email
          </p>
          <VerificationCodeForm
            verify={handleVerify}
            email={userEmail}
            callResend={handleEmailSubmission}
            isMobile={isMobile}
          />
        </>
      );
    }

    return (
      <>
        <p className={subtextClass}>
          Enter Your email for the verification process, we will send 4 digits code to Your email.
        </p>
        <SubmitEmail submitEmail={handleEmailSubmission} isMobile={isMobile} />
      </>
    );
  };

  if (!isMobile) {
    // DESKTOP VIEW FORGOT PASSWORD FORM
    return (
      <div className="flex flex-col bg-white rounded-[60px] pt-10 pb-12 w-[40rem] h-[calc(100%-64px)] min-h-[46rem] shadow-block-shadow my-8 relative px-8">
        <div
          onClick={showLogin}
          className="absolute left-[30px] top-16 w-10 h-10 bg-white rounded-lg shadow-block-shadow flex items-center justify-center cursor-pointer z-10"
        >
          <IconSmallArrow className="w-2 h-4" />
        </div>
        <img className="w-48 mb-6 self-center object-contain" src={Logo} alt="pw25 logo" />
        <div
          className={classNames(
            'flex-1 relative w-full flex flex-col py-8 border-b border-t border-color-divider px-20 self-center items-center',
            'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-divider before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
            'after:content-[""] after:absolute after:w-4 after:h-4 after:rounded-full after:border after:border-color-divider after:bg-white after:z-10 after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:translate-y-1/2',
          )}
        >
          <p className="mb-4 nunito-title text-22">{verifiedCode ? 'Reset' : 'Forgot'} Password</p>
          {storeModalBody()}
        </div>
        <SmallButton className="self-center !mt-7" label="Back to Login" onClick={showLogin} />
      </div>
    );
  }

  // MOBILE VIEW FORGOT PASSWORD FORM
  return (
    <div className="flex flex-col bg-white rounded-3xl pt-6 pb-6 w-full max-w-[340px] shadow-block-shadow my-auto relative px-4">
      <div
        onClick={showLogin}
        className="absolute left-4 top-5 w-8 h-8 bg-white rounded-lg shadow-block-shadow flex items-center justify-center cursor-pointer z-20"
      >
        <IconSmallArrow className="w-2 h-4" />
      </div>
      <img className="w-32 mb-4 self-center object-contain" src={LogoClean} alt="pw25 logo" />
      <div
        className={classNames(
          'flex-1 relative w-full flex flex-col py-5 border-b border-t border-color-divider px-3 self-center items-center',
          'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-divider before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
          'after:content-[""] after:absolute after:w-4 after:h-4 after:rounded-full after:border after:border-color-divider after:bg-white after:z-10 after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:translate-y-1/2',
        )}
      >
        <p className="mb-2 nunito-title text-20">{verifiedCode ? 'Reset' : 'Forgot'} Password</p>
        {storeModalBody()}
      </div>
      <SmallButton className="self-center !mt-5" label="Back to Login" onClick={showLogin} />
    </div>
  );
};

const SubmitEmail = ({ submitEmail }) => {
  const [btnDisabled, setBtnDisabled] = useState(true);

  const onValuesChange = (_, allValues) => {
    const email = allValues.email;
    setBtnDisabled(!(email !== undefined && email !== ''));
  };

  const handleEmailSubmit = async (values) => {
    const email = values.email;
    await submitEmail(email);
  };

  return (
    <Form
      className="mt-6 md:mt-16 flex flex-col items-center w-full gap-y-4"
      name="forgot-password"
      onFinish={handleEmailSubmit}
      onValuesChange={onValuesChange}
      autoComplete="off"
      requiredMark={'optional'}
      clearOnDestroy={false}
    >
      <FormInput
        className="w-full"
        label="Email"
        name="email"
        placeholder="Email"
        required
        errorMessage="Please enter valid email"
      />
      <OrangeButton
        type="submit"
        className="self-center mt-4 md:mt-10"
        btnClassName="ant-btn-green font-semibold text-14 !h-[40px] rounded-xl px-8 min-w-[140px]"
        label="Send"
        disabled={btnDisabled}
      />
    </Form>
  );
};

const VerificationCodeForm = ({ verify, email, callResend }) => {
  const [code, setCode] = useState(['', '', '', '']);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (inputsRef.current && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, []);

  const handleInputChange = (e, index) => {
    const value = e.target.value;
    if (value.length <= 1) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      if (value && index < inputsRef.current.length - 1) {
        inputsRef.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const isCodeComplete = code.every((digit) => digit !== '');

  const handleVerifyCode = () => {
    const verificationCode = code.join('');

    verify(verificationCode);
  };

  const handleResend = async () => {
    await callResend(email);
  };

  return (
    <div className="flex flex-col mt-6 md:mt-16 items-center w-full">
      <div className="flex flex-col items-center w-full max-w-[280px]">
        <p className="text-14 font-medium mb-2 self-start">Code</p>
        <Form className="flex flex-col items-center w-full" onFinish={handleVerifyCode}>
          <div className="flex gap-x-3 md:gap-x-6 justify-center w-full">
            {[0, 1, 2, 3].map((_, index) => (
              <Form.Item key={index}>
                <Input
                  className="verification-input"
                  type="number"
                  maxLength={1}
                  ref={(el) => (inputsRef.current[index] = el)}
                  onChange={(e) => handleInputChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              </Form.Item>
            ))}
          </div>
          <button type="button" className="text-14 font-medium text-blue mt-4 md:mt-7" onClick={handleResend}>
            Resend
          </button>
          <OrangeButton
            type="submit"
            className="self-center mt-4 md:mt-10"
            btnClassName="ant-btn-green font-semibold text-14 !h-[40px] rounded-xl px-8 min-w-[140px]"
            label="Verify"
            disabled={!isCodeComplete}
          />
        </Form>
      </div>
    </div>
  );
};

export const ResetPassword = ({ reset }) => {
  const [btnDisabled, setBtnDisabled] = useState(true);

  const onValuesChange = (_, allValues) => {
    const { password, confirm_password } = allValues;
    const isPassesMatching = password === confirm_password;
    const isDisabled =
      password !== undefined &&
      password !== '' &&
      confirm_password !== undefined &&
      confirm_password !== '';
    setBtnDisabled(!(isDisabled && isPassesMatching));
  };

  const handleReset = async (allValues) => {
    const { password } = allValues;

    await reset(password);
  };

  return (
    <Form
      onFinish={handleReset}
      requiredMark={'optional'}
      onValuesChange={onValuesChange}
      className="flex flex-col w-full mt-16"
    >
      <div className="flex flex-col gap-y-6">
        <FormInput
          type="password"
          label="New Password"
          name="password"
          placeholder="Password"
          required
        />
        <FormInput
          type="password"
          label="Confirm Password"
          name="confirm_password"
          placeholder="Password"
          required
        />
      </div>
      <OrangeButton
        type="submit"
        className="self-center mt-16"
        label="Save"
        disabled={btnDisabled}
      />
    </Form>
  );
};
